import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";

const STORAGE_KEY = "rem7_promo_seen";
const DELAY_MS = 8000;
const NAVY = "#15283E";
const GOLD = "#C8A76D";

const copy = {
  en: {
    eyebrow: "Spain pre-launch offer",
    title: "REM7 Smart Toilet",
    oldPrice: "1.800 €",
    oldLabel: "Previous price",
    newPrice: "600 €",
    line: "Limited edition: only 130 units in production.",
    whatsapp: "Message us on WhatsApp",
    contact: "Contact form",
    waText: "Hello, I'm interested in the REM7 Spain pre-launch offer.",
    alt: "REM7 smart toilet by Aureum VII Trading",
  },
  es: {
    eyebrow: "Oferta de prelanzamiento en España",
    title: "REM7 Smart Toilet",
    oldPrice: "1.800 €",
    oldLabel: "Precio anterior",
    newPrice: "600 €",
    line: "Edición limitada: solo 130 unidades en producción.",
    whatsapp: "Escríbenos por WhatsApp",
    contact: "Formulario de contacto",
    waText: "Hola, me interesa la oferta de prelanzamiento del REM7 en España.",
    alt: "Inodoro inteligente REM7 de Aureum VII Trading",
  },
};

/** Homepage (/:lang) and product pages (/:lang/products/...) only. */
const isEligiblePath = (pathname: string) => {
  const rest = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "").replace(/\/+$/, "");
  return rest === "" || rest.startsWith("/products/");
};

const alreadySeen = () => {
  try { return !!sessionStorage.getItem(STORAGE_KEY); } catch { return true; }
};

const Rem7PrelaunchPopup = () => {
  const { language } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const c = language === "es" ? copy.es : copy.en;
  const eligible = isEligiblePath(pathname);

  useEffect(() => {
    if (!eligible || alreadySeen()) return;
    let done = false;
    const show = () => {
      if (done || alreadySeen()) return;
      done = true;
      try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
      setOpen(true);
      cleanup();
    };
    const timer = window.setTimeout(show, DELAY_MS);
    const desktop = window.matchMedia?.("(pointer: fine)").matches;
    const onLeave = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) show();
    };
    if (desktop) document.addEventListener("mouseout", onLeave);
    function cleanup() {
      window.clearTimeout(timer);
      document.removeEventListener("mouseout", onLeave);
    }
    return cleanup;
  }, [eligible]);

  const waHref = `https://wa.me/34688495309?text=${encodeURIComponent(c.waText)}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-h-[90vh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-lg border p-0 font-inter motion-reduce:animate-none motion-reduce:transition-none [&>button]:text-[#C8A76D] [&>button]:opacity-90 [&>button]:h-11 [&>button]:w-11 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:right-2 [&>button]:top-2"
        style={{ backgroundColor: NAVY, borderColor: GOLD, color: "#F5F1E8" }}
      >
        <img
          src="/assets/rem7-email-hero-product.jpg"
          alt={c.alt}
          className="h-48 w-full rounded-t-lg object-contain sm:h-60"
          style={{ backgroundColor: "#F5F1E8" }}
        />
        <div className="space-y-4 p-5 sm:p-7">
          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }}>{c.eyebrow}</p>
          <DialogTitle className="font-playfair text-2xl leading-tight sm:text-3xl">{c.title}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3">
              <p className="flex items-baseline gap-3">
                <span className="sr-only">{c.oldLabel}: </span>
                <s className="text-lg" style={{ color: "#A9A396" }}>{c.oldPrice}</s>
                <span className="font-playfair text-4xl font-semibold" style={{ color: GOLD }}>{c.newPrice}</span>
              </p>
              <p className="text-base" style={{ color: "#D9D4C8" }}>{c.line}</p>
            </div>
          </DialogDescription>
          <div className="flex flex-col gap-3 pt-1">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md px-4 py-3 font-semibold"
              style={{ backgroundColor: GOLD, color: NAVY }}
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" /> {c.whatsapp}
            </a>
            <LocalizedLink
              to="/contact#enquiry-form"
              onClick={() => setOpen(false)}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border px-4 py-3 font-semibold"
              style={{ borderColor: GOLD, color: GOLD }}
            >
              <Mail className="h-4 w-4" aria-hidden="true" /> {c.contact}
            </LocalizedLink>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Rem7PrelaunchPopup;
